import React, { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SignalRService } from '../services/signalrService';
import { apiService } from '../services/api';
import { customerPortalQueryKeys } from '../hooks/useCustomerPortal';

interface CustomerPortalSignalRContextType {
  signalR: SignalRService;
  isConnected: boolean;
  connectionState: { ticket: string; notification: string };
}

const CustomerPortalSignalRContext = createContext<CustomerPortalSignalRContextType | undefined>(undefined);

// Create a separate SignalR service instance for the customer portal
const customerPortalSignalRService = new SignalRService();

interface CustomerPortalSignalRProviderProps {
  children: ReactNode;
  organizationId: string;
}

/**
 * Gets the customer portal token from localStorage
 */
const getCustomerToken = (organizationId: string): string | null => {
  return localStorage.getItem(`customerToken_${organizationId}`);
};

/**
 * Validates a JWT token to ensure it's not expired and has required claims
 */
const validateCustomerToken = (token: string, organizationId: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1]));

    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) return false;

    // Check for required customer portal claims
    if (payload.customer_portal !== 'true') return false;
    if (!payload.customer_id) return false;
    if (!payload.organization_id) return false;

    // Verify organization ID matches
    if (payload.organization_id !== organizationId) return false;

    return true;
  } catch {
    return false;
  }
};

/**
 * Checks if the current path is a customer portal route
 */
const isPortalRoute = (path: string): boolean => {
  return path.includes('/portal');
};

export const CustomerPortalSignalRProvider: React.FC<CustomerPortalSignalRProviderProps> = ({
  children,
  organizationId
}) => {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState({ ticket: 'Disconnected', notification: 'Disconnected' });
  // Ref to prevent React Strict Mode double-initialization
  const initializationAttemptedRef = useRef(false);

  // Helper to update connection state
  const updateConnectionState = () => {
    const connected = customerPortalSignalRService.isConnected;
    const state = customerPortalSignalRService.connectionState;
    setIsConnected(connected);
    setConnectionState(state);
  };

  useEffect(() => {
    // Prevent React Strict Mode double-initialization
    if (initializationAttemptedRef.current) {
      return;
    }
    initializationAttemptedRef.current = true;

    let cleanupConnectionCallback: (() => void) | undefined;

    const initializeSignalR = async () => {
      const currentPath = window.location.pathname;
      const token = getCustomerToken(organizationId);

      if (!token) {
        return;
      }

      if (!isPortalRoute(currentPath)) {
        // Disconnect if not on portal route
        if (customerPortalSignalRService.isConnected) {
          await customerPortalSignalRService.disconnect();
          setIsConnected(false);
          setConnectionState({ ticket: 'Disconnected', notification: 'Disconnected' });
        }
        return;
      }

      // Validate the token before attempting connection
      if (!validateCustomerToken(token, organizationId)) {
        // Clear invalid token
        localStorage.removeItem(`customerToken_${organizationId}`);
        return;
      }

      try {
        await customerPortalSignalRService.initialize(token);

        // Subscribe to connection state changes for immediate updates
        cleanupConnectionCallback = customerPortalSignalRService.onConnectionStateChange(updateConnectionState);

        // Check immediately after initialization
        updateConnectionState();
      } catch {
        // Silently handle initialization errors
      }
    };

    initializeSignalR();

    return () => {
      cleanupConnectionCallback?.();
      // Reset on cleanup so re-mount can initialize again if needed
      initializationAttemptedRef.current = false;
    };
  }, [organizationId]);

  // Listen for route changes to connect/disconnect SignalR appropriately
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      const token = getCustomerToken(organizationId);
      const onPortalRoute = isPortalRoute(currentPath);

      if (token && onPortalRoute && !customerPortalSignalRService.isConnected) {
        // Validate token before connecting
        if (!validateCustomerToken(token, organizationId)) {
          return;
        }

        // We're on a portal route but not connected - connect
        customerPortalSignalRService.initialize(token)
          .then(() => {
            updateConnectionState();
          })
          .catch(() => {
            // Silently handle initialization errors
          });
      } else if ((!token || !onPortalRoute) && customerPortalSignalRService.isConnected) {
        // We're not on a portal route or have no token but are connected - disconnect
        customerPortalSignalRService.disconnect()
          .then(() => {
            setIsConnected(false);
            setConnectionState({ ticket: 'Disconnected', notification: 'Disconnected' });
          });
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args: Parameters<typeof originalPushState>) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    history.replaceState = function(...args: Parameters<typeof originalReplaceState>) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [organizationId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      customerPortalSignalRService.disconnect();
    };
  }, []);

  // Global ticket event handlers - always active regardless of current page
  // This ensures cache is invalidated even when customer is on a different route
  useEffect(() => {
    if (!isConnected) return;

    // Define event data types locally
    interface TicketEventData {
      ticketId?: string;
      TicketId?: string;
    }

    const handleGlobalTicketUpdated = (data: TicketEventData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[CustomerPortal SignalR Global] Ticket updated', { ticketId });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.stats() });
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.insights() });
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.activity() });

        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
        }
      }, 250);
    };

    const handleGlobalCommentAdded = (data: TicketEventData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[CustomerPortal SignalR Global] Comment added', { ticketId });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
        queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.activity() });

        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
        }
      }, 250);
    };

    const handleGlobalTicketCreated = () => {
      console.log('[CustomerPortal SignalR Global] Ticket created');
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.stats() });
      queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.insights() });
    };

    const handleGlobalParticipantsChanged = (data: TicketEventData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[CustomerPortal SignalR Global] Participants changed', { ticketId });

      setTimeout(() => {
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
        }
      }, 250);
    };

    const handleGlobalCommentAttachmentAdded = (data: TicketEventData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[CustomerPortal SignalR Global] Attachment added', { ticketId });

      setTimeout(() => {
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: customerPortalQueryKeys.tickets.detail(ticketId) });
        }
      }, 250);
    };

    // Register global handlers for all ticket-related events
    const cleanups = [
      customerPortalSignalRService.on('TicketUpdated', handleGlobalTicketUpdated),
      customerPortalSignalRService.on('TicketCreated', handleGlobalTicketCreated),
      customerPortalSignalRService.on('TicketActivity', handleGlobalTicketUpdated),
      customerPortalSignalRService.on('CommentAdded', handleGlobalCommentAdded),
      customerPortalSignalRService.on('ParticipantsChanged', handleGlobalParticipantsChanged),
      customerPortalSignalRService.on('CommentAttachmentAdded', handleGlobalCommentAttachmentAdded),
    ];

    return () => {
      cleanups.forEach(cleanup => cleanup());
    };
  }, [isConnected, queryClient]);

  // The server revoked this customer's portal session (e.g. the customer was deleted) - end it now
  useEffect(() => {
    if (!isConnected) return;

    return customerPortalSignalRService.on('SessionRevoked', () => {
      apiService.endSession();
    });
  }, [isConnected]);

  const contextValue: CustomerPortalSignalRContextType = {
    signalR: customerPortalSignalRService,
    isConnected,
    connectionState,
  };

  return (
    <CustomerPortalSignalRContext.Provider value={contextValue}>
      {children}
    </CustomerPortalSignalRContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCustomerPortalSignalR = (): CustomerPortalSignalRContextType => {
  const context = useContext(CustomerPortalSignalRContext);
  if (context === undefined) {
    throw new Error('useCustomerPortalSignalR must be used within a CustomerPortalSignalRProvider');
  }
  return context;
};
